import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Trophy, Users, Dumbbell, Utensils } from 'lucide-react';

interface TopRoutine {
  id: string;
  title: string;
  category: string;
  likes_count: number;
  profile: {
    id: string;
    name: string | null;
    avatar_url: string | null;
  };
}

interface FollowedAthlete {
  followed_id: string;
  profile: {
    id: string;
    name: string | null;
    avatar_url: string | null;
    username: string | null;
  };
}

interface TrainingSidebarProps {
  topRoutines: TopRoutine[];
  followedAthletes: FollowedAthlete[];
}

export const TrainingSidebar: React.FC<TrainingSidebarProps> = ({
  topRoutines,
  followedAthletes,
}) => {
  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="space-y-6">
      {/* Top Routines This Week */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Trophy className="h-4 w-4 text-secondary" />
            Top Routines This Week
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {topRoutines.length === 0 ? (
            <p className="text-sm text-muted-foreground">No routines yet this week</p>
          ) : (
            topRoutines.map((routine, idx) => (
              <div key={routine.id} className="flex items-center gap-3">
                <span className="text-lg font-bold text-muted-foreground w-5">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{routine.title}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{routine.profile?.name || 'Athlete'}</span>
                    <span>•</span>
                    <span className="flex items-center">
                      ❤️ {routine.likes_count}
                    </span>
                  </div>
                </div>
                <Badge variant="outline" className="shrink-0">
                  {routine.category === 'workout' ? (
                    <Dumbbell className="h-3 w-3" />
                  ) : (
                    <Utensils className="h-3 w-3" />
                  )}
                </Badge>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Athletes You Follow */}
      {followedAthletes.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              Athletes You Follow
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {followedAthletes.map((follow) => (
              <Link
                key={follow.followed_id}
                to={`/athletes/${follow.profile?.username || follow.followed_id}`}
                className="flex items-center gap-3 hover:bg-muted/50 rounded-lg p-1 -m-1 transition-colors"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={follow.profile?.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {getInitials(follow.profile?.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium truncate">
                  {follow.profile?.name || 'Athlete'}
                </span>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
